import { Observable } from 'rxjs';

type SseEvent = {
  event?: string;
  data: string;
};

export class SseHttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;

  constructor(args: { status: number; statusText: string; url: string }) {
    super(`SSE request failed: ${args.status} ${args.statusText}`);
    this.name = 'SseHttpError';
    this.status = args.status;
    this.statusText = args.statusText;
    this.url = args.url;
  }
}

function parseSseEvent(raw: string): SseEvent | null {
  const lines = raw.split(/\r?\n/);
  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line) continue;
    if (line.startsWith(':')) continue; // comment/keep-alive
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
      continue;
    }
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

export function streamSseText(url: string, init: RequestInit): Observable<SseEvent> {
  return new Observable<SseEvent>((observer) => {
    const ac = new AbortController();
    let buffer = '';

    void fetch(url, { ...init, signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) {
          observer.error(new SseHttpError({ status: res.status, statusText: res.statusText, url }));
          return;
        }
        if (!res.body) {
          observer.error(new Error('SSE response has no body'));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;

          buffer += decoder.decode(value, { stream: true });
          // Normalize all line endings (CRLF, bare CR) to LF per the SSE specification.
          buffer = buffer.replace(/\r\n|\r/g, '\n');

          // Events are separated by a blank line.
          let idx = buffer.indexOf('\n\n');
          while (idx !== -1) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const evt = parseSseEvent(chunk);
            if (evt) observer.next(evt);
            idx = buffer.indexOf('\n\n');
          }
        }

        // Flush any last partial event.
        const last = parseSseEvent(buffer.trim());
        if (last) observer.next(last);

        observer.complete();
      })
      .catch((err) => observer.error(err));

    return () => ac.abort();
  });
}

export function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
